import type { DataModelLayout, LayoutSection } from '@/types/DataModel';

/** Asset layout listing the fields of the menu "Card view" (D-021, D-036). */
const DAM_CARD_VIEW_OPTIONS_LAYOUT = 'asset_view_options';

const BODY_SECTION = 'BODY';

type DamCardViewOption = LayoutSection['attributes'][number];

/** Selectable fields in layout order; hidden stubs are skipped. */
const cardViewOptionsOf = (optionsLayout: DataModelLayout | undefined): DamCardViewOption[] =>
    (optionsLayout?.sections ?? []).flatMap((section) => section.attributes ?? []).filter((attribute) => !attribute.hidden);

const cardAttributeNames = (cardLayout: DataModelLayout | undefined): string[] =>
    (cardLayout?.sections ?? []).flatMap((section) => (section.attributes ?? []).map((attribute) => attribute.name));

/** The default selection: every option the card layout already shows. */
const defaultCardViewSelection = (cardLayout: DataModelLayout | undefined, options: DamCardViewOption[]): string[] => {
    const shown = cardAttributeNames(cardLayout);
    return options.map((option) => option.name).filter((name) => shown.includes(name));
};

/**
 * Keeps the known option names of a stored selection, in option order; `undefined` if nothing
 * usable is stored. Default fields the user has not seen when saving (`known`, all options at
 * that time) are added, so fields a data model adds to the card show up. Selections stored
 * without `known` count every unselected default as new.
 */
const normalizeCardViewSelection = (
    stored: unknown,
    options: DamCardViewOption[],
    defaults: string[] = [],
    known?: unknown,
): string[] | undefined => {
    if (!Array.isArray(stored)) {
        return undefined;
    }
    const knownNames: unknown[] = Array.isArray(known) ? known : stored;
    return options
        .map((option) => option.name)
        .filter((name) => stored.includes(name) || (defaults.includes(name) && !knownNames.includes(name)));
};

const isSameSelection = (a: string[], b: string[]) => a.length === b.length && a.every((name) => b.includes(name));

/**
 * Card layout with the selection applied: options that are not selected disappear from
 * their area, selected options the card does not show yet are appended to `BODY`.
 * Card attributes that are not options stay untouched.
 */
const applyCardViewSelection = (
    cardLayout: DataModelLayout | undefined,
    options: DamCardViewOption[],
    selection: string[],
): DataModelLayout | undefined => {
    if (!cardLayout) {
        return cardLayout;
    }
    const optionNames = options.map((option) => option.name);
    const hidden = optionNames.filter((name) => !selection.includes(name));
    const shown = cardAttributeNames(cardLayout);
    const extra = options.filter((option) => selection.includes(option.name) && !shown.includes(option.name));

    const sections = cardLayout.sections.map((section) => ({
        ...section,
        attributes: (section.attributes ?? []).filter((attribute) => !hidden.includes(attribute.name)),
    }));
    if (extra.length > 0) {
        const body = sections.find((section) => section.name === BODY_SECTION);
        if (body) {
            body.attributes = [...body.attributes, ...extra];
        } else {
            sections.push({ name: BODY_SECTION, label: null, model_defined: true, attributes: extra });
        }
    }
    return { ...cardLayout, sections };
};

export {
    applyCardViewSelection,
    cardViewOptionsOf,
    DAM_CARD_VIEW_OPTIONS_LAYOUT,
    defaultCardViewSelection,
    isSameSelection,
    normalizeCardViewSelection,
};
export type { DamCardViewOption };
